import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ReactCountryFlag from 'react-country-flag'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  organizationDetailsSchema,
  type OrganizationDetails,
} from '@/features/auth/auth-model'
import { NonEuCountrySelect } from '@/features/onboarding/non-eu-country-select'
import { useDoesOrganizationRequiresLocalAddress } from '@/shared/hooks/api/organizations'
import { validateLatinCharacters } from '@/shared/lib/latinCharacterValidation'

type OrganizationDetailsPageProps = {
  next: (payload: OrganizationDetails) => void
  initialState: OrganizationDetails
}

export function OrganizationDetailsPage({
  next,
  initialState,
}: OrganizationDetailsPageProps) {
  const { t } = useTranslation()
  const [state, setState] = useState<OrganizationDetails>(initialState)
  const { placeOfRegistration, organizationName, localName } = state
  const doesOrganizationRequiresLocalAddress =
    useDoesOrganizationRequiresLocalAddress(placeOfRegistration)

  const onInputChange = useCallback(
    (
      event:
        | React.ChangeEvent<HTMLInputElement>
        | React.ChangeEvent<HTMLSelectElement>
    ) => {
      if (!doesOrganizationRequiresLocalAddress && event.target.id === 'organizationName') {
        setState({
          ...state,
          organizationName: event.target.value,
          localName: event.target.value,
        })
        return
      }
      setState({ ...state, [event.target.id]: event.target.value })
    },
    [state, doesOrganizationRequiresLocalAddress]
  )

  const onPlaceOfRegistrationChange = useCallback(
    (value: string) => {
      setState({
        ...state,
        placeOfRegistration: value,
        localName: '',
        organizationName: '',
      })
    },
    [state]
  )

  const onNextClick = useCallback(() => {
    next(state)
  }, [state, next])

  const isNextEnabled = useMemo(() => {
    return organizationDetailsSchema.safeParse(state).success
  }, [state])

  const organizationNameError = validateLatinCharacters(
    organizationName,
    t('common:errors.latinCharacters')
  )

  return (
    <>
      <Card className="w-full">
        <CardContent className="space-y-4">
          <p className="text-sm leading-relaxed text-muted-foreground">
            {t('onboarding:inputFields.placeOfRegistration.tooltip')}
          </p>
          <NonEuCountrySelect
            label={t('onboarding:inputFields.placeOfRegistration.label')}
            value={placeOfRegistration}
            onChange={onPlaceOfRegistrationChange}
            tooltip={t('onboarding:inputFields.placeOfRegistration.tooltip')}
          />
          {!doesOrganizationRequiresLocalAddress ? (
            <div className="grid gap-2">
              <Label htmlFor="organizationName">
                {t('onboarding:inputFields.organizationName.label')}
              </Label>
              <Input
                id="organizationName"
                disabled={placeOfRegistration.length <= 0}
                value={organizationName}
                onChange={onInputChange}
                aria-invalid={!!organizationNameError}
              />
              {organizationNameError ? (
                <p className="text-sm text-destructive">{organizationNameError}</p>
              ) : (
                <p className="text-sm text-muted-foreground">
                  {t('onboarding:inputFields.organizationName.hint')}
                </p>
              )}
            </div>
          ) : (
            <>
              <div className="grid gap-2">
                <Label htmlFor="localName" className="flex items-center gap-2">
                  {t('onboarding:inputFields.localName.label')}
                  <ReactCountryFlag countryCode={placeOfRegistration} svg />
                </Label>
                <Input
                  id="localName"
                  value={localName}
                  onChange={onInputChange}
                  placeholder={t('onboarding:inputFields.localName.placeholder')}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="organizationName" className="flex items-center gap-2">
                  {t('onboarding:inputFields.organizationName.label')}
                  <ReactCountryFlag countryCode="gb" svg />
                </Label>
                <Input
                  id="organizationName"
                  disabled={placeOfRegistration.length <= 0}
                  value={organizationName}
                  onChange={onInputChange}
                  placeholder={t('onboarding:inputFields.organizationName.placeholder')}
                  aria-invalid={!!organizationNameError}
                />
                {organizationNameError ? (
                  <p className="text-sm text-destructive">{organizationNameError}</p>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    {t('onboarding:inputFields.requiresLocalNameHint')}
                  </p>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>
      <div className="mt-6 flex justify-end">
        <Button onClick={onNextClick} disabled={!isNextEnabled}>
          {t('common:next')}
        </Button>
      </div>
    </>
  )
}
